import { Component, EventEmitter, Input, Output } from '@angular/core'

import { LEDGER_VIEW_LABELS, LeaseLedger } from '@modules/private/lease/data/interfaces/lease-ledger.interface'
import { ApiPaymentService } from '@modules/private/payment/data/services/api/api-payment.service'

/**
 * El detalle contable de UN arrendamiento, abierto desde Cobranza.
 *
 * ## Por qué existe, teniendo ya la pestaña de Exigibilidad
 *
 * Contabilidad ve los totales por contrato en Cartera y en CENTRAL, pero **no puede entrar al
 * expediente**: `/lease/:id` tiene 19 pestañas que no están gateadas una por una, así que darles
 * entrada sería darles Comité, Negociación, Algoritmo y Análisis de riesgo. Este diálogo es la otra
 * mitad de esa decisión — ven el «de qué» sin ver lo operativo.
 *
 * ## Lo que reusa, y por qué no se copió
 *
 * La franja de totales es `app-ledger-strip`, el MISMO componente que pinta la pestaña de
 * Exigibilidad. Recibe datos planos, así que sirve tal cual. Copiarlo habría creado dos franjas que
 * dicen lo mismo y se separan a la primera corrección — que es justo el defecto que el chip de estado
 * ya tuvo repartido en seis archivos.
 *
 * **Es solo lectura.** Aplicar, anular o condonar sigue siendo del centro de pagos, con su bitácora y
 * su prelación: Contabilidad ni siquiera tiene el permiso.
 */
@Component({
  selector: 'app-lease-ledger-dialog',
  templateUrl: './lease-ledger-dialog.component.html',
  styleUrls: ['./lease-ledger-dialog.component.scss'],
})
export class LeaseLedgerDialogComponent {
  @Input() visible = false
  @Output() visibleChange = new EventEmitter<boolean>()

  ledger: LeaseLedger | null = null
  cargando = false
  error = ''
  folio = ''

  constructor(private readonly api: ApiPaymentService) {}

  /** Abre el libro mayor de un arrendamiento. El folio se pasa para poder titular antes de que cargue. */
  abrir(leaseId: string, folio: string): void {
    this.folio = folio
    this.ledger = null
    this.error = ''
    this.visible = true
    this.visibleChange.emit(true)
    this.cargar(leaseId)
  }

  cerrar(): void {
    this.visible = false
    this.visibleChange.emit(false)
  }

  private cargar(leaseId: string): void {
    this.cargando = true
    this.api.getLedger(leaseId).subscribe((r) => {
      this.cargando = false
      if (r.error || !r.data) {
        this.error = r.msg || 'No se pudo cargar el libro mayor'
        return
      }
      this.ledger = r.data
    })
  }

  /** Con el interruptor apagado en pausa, la mora se muestra pero no entra al pendiente (F27). */
  get moraPausada(): boolean {
    return !!this.ledger && !this.ledger.settings?.moraEnabled
  }

  /** Solo las letras que deben algo: el detalle contable es sobre lo que falta, no sobre lo saldado. */
  get periodosConSaldo(): any[] {
    return (this.ledger?.periods || []).filter((p: any) => (p.pending ?? 0) > 0.01)
  }

  /*
   * El estado de la letra, DERIVADO.
   *
   *! Pintaba `status`, que es el campo guardado, y enseñaba «PAGADO» en todas las letras de un
   *! arrendamiento cuyas letras deben la renta entera — un chip verde junto a «Falta $37,228.91» en
   *! rojo, en la misma fila. El estado que corresponde con esas cifras es `view`, el mismo que usa
   *! la pestaña de Exigibilidad; las etiquetas se comparten para que las dos pantallas no se
   *! separen a la primera corrección.
   */
  etiqueta(p: any): string {
    return LEDGER_VIEW_LABELS[p?.view] ?? p?.view ?? '—'
  }

  severidad(p: any): string {
    const v = p?.view
    if (v === 'PAID') return 'success'
    if (v === 'OVERDUE') return 'danger'
    return v === 'ANTICIPATED' ? 'info' : 'warning'
  }
}
